import React, { useMemo } from 'react';
import { useIntl, MessageDescriptor } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faVideo } from '@fortawesome/pro-light-svg-icons/faVideo';
import { faSparkles } from '@fortawesome/pro-light-svg-icons/faSparkles';
import { faAlignLeft } from '@fortawesome/pro-light-svg-icons/faAlignLeft';
import { faFilm } from '@fortawesome/pro-light-svg-icons/faFilm';
import { TypedMemo } from '../../../../common/types';
import { addSongFlowMessages } from '../addSongFlowMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from '../CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AudioUnlocksProps {
  /** Full-song videos need Pro (`canRenderFullSongVideos`); false shows the plan chip. */
  canRenderFullSongVideos: boolean;
}

interface Unlock {
  key: string;
  icon: IconDefinition;
  title: MessageDescriptor;
  body: MessageDescriptor;
  needsPro: boolean;
}

/** What the master unlocks (design catalog-add-song e1, e4): plain facts, not buttons. */
const AudioUnlocksComponent: React.FC<AudioUnlocksProps> = ({ canRenderFullSongVideos }) => {
  const intl = useIntl();
  const unlocks = useMemo<Unlock[]>(
    () => [
      {
        key: 'studio',
        icon: faVideo,
        title: addSongFlowMessages.audioStudio,
        body: addSongFlowMessages.audioStudioBody,
        needsPro: false,
      },
      {
        key: 'reel',
        icon: faSparkles,
        title: addSongFlowMessages.audioReel,
        body: addSongFlowMessages.audioReelBody,
        needsPro: false,
      },
      {
        key: 'sync',
        icon: faAlignLeft,
        title: addSongFlowMessages.audioSync,
        body: addSongFlowMessages.audioSyncBody,
        needsPro: false,
      },
      {
        key: 'full',
        icon: faFilm,
        title: addSongFlowMessages.audioFull,
        body: addSongFlowMessages.audioFullBody,
        needsPro: !canRenderFullSongVideos,
      },
    ],
    [canRenderFullSongVideos]
  );

  return (
    <ul className={styles.unlocks}>
      {unlocks.map((unlock) => (
        <li key={unlock.key} className={styles.unlock}>
          <span className={styles.kbox}>
            <FontAwesomeIcon icon={unlock.icon} />
          </span>
          <div>
            <b>
              {intl.formatMessage(unlock.title)}
              {unlock.needsPro && (
                <span className={styles.lockChip}>{intl.formatMessage(addSongFlowMessages.audioPro)}</span>
              )}
            </b>
            <small>{intl.formatMessage(unlock.body)}</small>
          </div>
        </li>
      ))}
    </ul>
  );
};

export const AudioUnlocks = TypedMemo(AudioUnlocksComponent);
